import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Users, BookOpen, Star, ChevronLeft, Search, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { formatName, resolveImg } from '@/lib/helpers'

const filterOptions = ['Бүгд', 'Гүн ухаан', 'Бясалгал', 'Судар', 'Ритуал']

export default function Teachers() {
  const [activeFilter, setActiveFilter] = useState('Бүгд')
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [teachers, setTeachers] = useState<any[]>([])
  const [featuredTeacher, setFeaturedTeacher] = useState<any>(null)
  const [pagination, setPagination] = useState<any>({ pages: 1 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.teachers.featured().then(setFeaturedTeacher).catch(console.error)
  }, [])

  useEffect(() => {
    setLoading(true)
    api.teachers.list({ page: currentPage, limit: 12, specialty: activeFilter, search: searchQuery })
      .then(res => {
        setTeachers(res.data)
        setPagination(res.pagination)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [currentPage, activeFilter, searchQuery])

  return (
    <div>
      <section className="max-w-[1024px] mx-auto px-4 pt-8 pb-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ChevronLeft className="size-4" />
          Нүүр хуудас
        </Link>
        <h1 className="font-display font-black text-3xl text-gray-900 mb-2">Эрдэм мэдлэгтэй багш нараас суралц</h1>
        <p className="text-sm text-gray-500">Таныг дотоод амар амгаланд хөтлөх, олон жилийн туршлагатай эрдэмтэн багш нар</p>
      </section>

      {featuredTeacher && (
        <section className="max-w-[1024px] mx-auto px-4 py-8">
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-100 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 sm:gap-8 items-center">
            <div className="w-32 h-32 sm:w-44 sm:h-44 rounded-2xl bg-white/80 shrink-0 overflow-hidden ring-2 ring-primary-100">
              {featuredTeacher.userId?.avatar ? (
                <img
                  src={resolveImg(featuredTeacher.userId.avatar)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="size-12 text-gray-300" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <span className="inline-block bg-primary-50 text-primary-600 text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">Онцлох багш</span>
              <h2 className="font-bold text-2xl text-gray-900 mt-3">{formatName(featuredTeacher.userId?.fullName)}</h2>
              <p className="text-sm text-primary-500 font-medium mt-1">{featuredTeacher.title || featuredTeacher.specialty}</p>
              <p className="text-sm text-gray-500 mt-3 line-clamp-2">{featuredTeacher.userId?.bio || featuredTeacher.description}</p>
              <div className="flex items-center gap-6 mt-5">
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-800">{featuredTeacher.followerCount ?? featuredTeacher.studentCount ?? 0}</p>
                  <p className="text-xs text-gray-400">Дагагч</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-800">{featuredTeacher.courseCount ?? 0}</p>
                  <p className="text-xs text-gray-400">Сургалт</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-800">{featuredTeacher.postCount ?? featuredTeacher.reviewCount ?? 0}</p>
                  <p className="text-xs text-gray-400">Пост</p>
                </div>
              </div>
              <Link
                to={`/teachers/${featuredTeacher._id}`}
                className="inline-block mt-5 bg-primary-500 text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary-600 transition"
              >
                Профайл үзэх
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1024px] mx-auto px-4 pb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {filterOptions.map(filter => (
            <button
              key={filter}
              onClick={() => { setActiveFilter(filter); setCurrentPage(1) }}
              className={`rounded-full px-5 py-2 text-sm font-medium transition ${
                activeFilter === filter ? 'bg-accent-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Багш хайх..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1) }}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
        </div>
      </section>

      <section className="max-w-[1024px] mx-auto px-4 pb-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-5 flex flex-col gap-3">
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="flex items-center gap-2 mt-2">
                    <div className="h-6 bg-gray-200 rounded-full w-16" />
                    <div className="h-6 bg-gray-200 rounded-full w-14" />
                    <div className="h-6 bg-gray-200 rounded-full w-14" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-28 mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {teachers.map(teacher => (
              <Link key={teacher._id} to={`/teachers/${teacher._id}`} className="group">
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition">
                  <div className="h-48 rounded-t-2xl bg-gradient-to-br from-gray-200 to-gray-300 relative flex items-center justify-center overflow-hidden">
                    {teacher.userId?.avatar ? (
                      <img
                        src={resolveImg(teacher.userId.avatar)}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <Users className="size-12 text-gray-400/40" />
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary-500 transition">{formatName(teacher.userId?.fullName)}</h3>
                    <p className="text-sm text-primary-500 mt-0.5">{teacher.specialty}</p>
                    <div className="flex items-center gap-2 mt-4">
                      <span className="bg-gray-100 text-gray-600 rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1">
                        <Users className="size-3" />{(teacher.followerCount ?? teacher.studentCount ?? 0).toLocaleString()}
                      </span>
                      <span className="bg-gray-100 text-gray-600 rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1">
                        <BookOpen className="size-3" />{teacher.courseCount ?? 0}
                      </span>
                      <span className="bg-gray-100 text-gray-600 rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1">
                        <Star className="size-3" />{(teacher.postCount ?? teacher.reviewCount ?? 0).toLocaleString()}
                      </span>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-500 group-hover:text-primary-600 transition">
                      Хичээлүүд үзэх <ChevronRight className="size-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-full text-sm font-medium transition ${
                  currentPage === page ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
